import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Paper, Typography, Box, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Chip, TextField, InputAdornment, TablePagination,
  FormControlLabel, Checkbox, FormControl, InputLabel, Select, MenuItem, TableSortLabel,
  Modal, List, ListItem, ListItemText, ListItemIcon, Divider, IconButton
} from '@mui/material';
import { Search, Visibility, VerifiedUser, Close, Warning, AutoFixHigh, Build, Download } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { expedientesApi, clientesApi } from '../services/api';
import { Expediente, Cliente } from '../types/expediente';

type ChipColor = 'success' | 'info' | 'error' | 'default' | 'primary' | 'secondary' | 'warning';
type Order = 'asc' | 'desc';
type SortableKey = keyof Expediente | 'proviene_de' | 'se_ha_convertido_en' | 'nivel';

// --- SOLUCIÓN AQUÍ: Interfaz mejorada para distinguir problemas de propuestas ---
interface VerificationIssue {
  expedienteId: string;
  codigo_actual: string;
  mensaje: string;
  tipo: 'Propuesta de Corrección' | 'Error sin solución automática';
  accionSugerida?: string; // Acción concreta para solucionar
}

const ExpedientesList: React.FC = () => {
  const [expedientes, setExpedientes] = useState<Expediente[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [hidePaid, setHidePaid] = useState(false);
  const [selectedEstado, setSelectedEstado] = useState('');
  const [selectedCliente, setSelectedCliente] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<SortableKey>('codigo_actual');

  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResults, setVerificationResults] = useState<VerificationIssue[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();
  // Mapa de clientes para búsqueda rápida
const clientesMap = useMemo(() => {
  const map = new Map<string, Cliente>();
  clientes.forEach(cliente => map.set(cliente.id, cliente));
  return map;
}, [clientes]);

// Función para obtener nombre del cliente
const getClienteNombre = useCallback((clienteId: string | undefined | null) => {
  if (!clienteId) return '-';
  const cliente = clientesMap.get(clienteId);
  return cliente ? cliente.nombre : 'Cliente no encontrado';
}, [clientesMap]);

  // Función para exportar expedientes a CSV
const exportToCSV = () => {
  const headers = ['Código Actual', 'Tipo', 'Número', 'Año', 'Cliente', 'Estado', 'Activo', 'Proviene', 'Convertido'];  
  const csvRows = sortedAndFilteredExpedientes.map(expediente => {
  const currentExp = expediente as any;
  return [
    currentExp.codigo_actual || '',
    currentExp.numero || '',
    currentExp.anio || '',
    currentExp.tipo || '',
    getClienteNombre(currentExp.cliente_id),
    currentExp.estado || '',
    currentExp.es_activo ? 'Sí' : 'No',
    currentExp.proviene_de || '',
    currentExp.se_ha_convertido_en || ''
  ].map(field => 
    typeof field === 'string' && (field.includes(';') || field.includes('"') || field.includes('\n'))
      ? `"${field.replace(/"/g, '""')}"` 
      : field
  ).join(';');
});
  
  const csvContent = [headers.join(';'), ...csvRows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  const today = new Date();
  const dateStr = today.getFullYear().toString() + 
                  (today.getMonth() + 1).toString().padStart(2, '0') + 
                  today.getDate().toString().padStart(2, '0');
  
  const filename = `expedientes_${dateStr}.csv`;
  
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};




  useEffect(() => {
    const loadExpedientes = async () => {
      try {
        setLoading(true);
        const data = await expedientesApi.getExpedientes(0, 10000); 
        setExpedientes(data);
      } catch (err) {
        setError('Error cargando expedientes');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadExpedientes();
  }, []);

  // Cargar clientes
useEffect(() => {
  const loadClientes = async () => {
    try {
      setLoadingClientes(true);
      const data = await clientesApi.getClientes(0, 1000);
      setClientes(data);
    } catch (err) {
      console.error('Error cargando clientes:', err);
      // No mostramos error para clientes, solo log
    } finally {
      setLoadingClientes(false);
    }
  };
  loadClientes();
}, []);



  const estadosDisponibles = [
    "Sol cobro a JZDO", "En Gerencia", "Pte de CLASIFICAR", "COBRADO",
    "Convertido", "Entregado", "Pendiente", "Proc Terminados"
  ];

  const sortedAndFilteredExpedientes = useMemo(() => {
  let filtered = expedientes.filter(exp => {
    const currentExp = exp as any;
    const lowercasedTerm = searchTerm.toLowerCase();
    
    const matchesSearch = !searchTerm || 
      currentExp.numero?.toString().includes(lowercasedTerm) ||
      currentExp.codigo_actual?.toLowerCase().includes(lowercasedTerm) ||
      currentExp.proviene_de?.toLowerCase().includes(lowercasedTerm) ||
      currentExp.se_ha_convertido_en?.toLowerCase().includes(lowercasedTerm) ||
      currentExp.tipo?.toLowerCase().includes(lowercasedTerm);
    
    const matchesActive = !showOnlyActive || currentExp.es_activo === true;
    const matchesPaid = !hidePaid || currentExp.cobrado === false;
    const matchesEstado = !selectedEstado || currentExp.estado === selectedEstado;
    
    // Nuevo filtro por cliente
    const matchesCliente = !selectedCliente || currentExp.cliente_id === selectedCliente;
    
    return matchesSearch && matchesActive && matchesPaid && matchesEstado && matchesCliente;
  });

    return filtered.sort((a, b) => {
      const valA = (a as any)[orderBy];
      const valB = (b as any)[orderBy];
      if (valA === null || valA === undefined) return 1;
      if (valB === null || valB === undefined) return -1;
      if (valB < valA) return order === 'asc' ? 1 : -1;
      if (valB > valA) return order === 'asc' ? -1 : 1;
      return 0;
    });
  }, [expedientes, searchTerm, showOnlyActive, hidePaid, selectedEstado, selectedCliente, order, orderBy]);

  // --- SOLUCIÓN AQUÍ: Lógica de verificación completamente rediseñada ---
  const handleVerifyData = () => {
    setIsVerifying(true);
    const issues: VerificationIssue[] = [];
    const expedientesPorCodigo = new Map(expedientes.map(exp => [(exp as any).codigo_actual, exp]));

    for (const exp of expedientes) {
      const currentExp = exp as any;

      // Regla 1 y 5 combinadas: "Convertido" sin destino y Relaciones rotas
      if (currentExp.estado === 'Convertido') {
        if (!currentExp.se_ha_convertido_en) {
          // Si no tiene destino, buscar si alguien apunta a él
          const hijoPotencial = expedientes.find(h => (h as any).proviene_de === currentExp.codigo_actual);
          if (hijoPotencial) {
            issues.push({
              expedienteId: currentExp.id,
              codigo_actual: currentExp.codigo_actual,
              mensaje: "Estado 'Convertido' pero 'Se ha convertido en' está vacío.",
              tipo: 'Propuesta de Corrección',
              accionSugerida: `Actualizar 'se_ha_convertido_en' con el valor '${(hijoPotencial as any).codigo_actual}'.`
            });
          } else {
            issues.push({
              expedienteId: currentExp.id,
              codigo_actual: currentExp.codigo_actual,
              mensaje: "Estado 'Convertido', pero no se encuentra ningún expediente que provenga de él.",
              tipo: 'Error sin solución automática'
            });
          }
        } else {
          // Si tiene destino, verificar que la relación sea mutua
          const hijo = expedientesPorCodigo.get(currentExp.se_ha_convertido_en);
          if (!hijo) {
            issues.push({ expedienteId: currentExp.id, codigo_actual: currentExp.codigo_actual, mensaje: `Apunta a un expediente hijo con código '${currentExp.se_ha_convertido_en}' que no existe.`, tipo: 'Error sin solución automática' });
          } else if ((hijo as any).proviene_de !== currentExp.codigo_actual) {
            issues.push({
              expedienteId: (hijo as any).id,
              codigo_actual: (hijo as any).codigo_actual,
              mensaje: `Este expediente dice provenir de '${(hijo as any).proviene_de}', pero el origen ('${currentExp.codigo_actual}') apunta a otro destino.`,
              tipo: 'Propuesta de Corrección',
              accionSugerida: `Corregir el campo 'proviene_de' con el valor '${currentExp.codigo_actual}'.`
            });
          }
        }
      }

      // Regla 2: Huérfano en la Jerarquía
      if (currentExp.nivel > 0 && !currentExp.proviene_de) {
        issues.push({ expedienteId: currentExp.id, codigo_actual: currentExp.codigo_actual, mensaje: "Nivel > 0 pero 'Proviene de' está vacío.", tipo: 'Error sin solución automática' });
      }

      // Regla 3: "Convertido" que sigue Activo
      if (currentExp.estado === 'Convertido' && currentExp.es_activo === true) {
        issues.push({
          expedienteId: currentExp.id,
          codigo_actual: currentExp.codigo_actual,
          mensaje: "Estado 'Convertido' pero sigue marcado como 'Activo'.",
          tipo: 'Propuesta de Corrección',
          accionSugerida: "Cambiar 'es_activo' a 'false'."
        });
      }

      // Regla 4: Cobrado pero no Terminado
      const estadosEnProceso = ["En Gerencia", "Pte de CLASIFICAR", "Pendiente"];
      if (currentExp.cobrado === true && estadosEnProceso.includes(currentExp.estado)) {
        issues.push({
          expedienteId: currentExp.id,
          codigo_actual: currentExp.codigo_actual,
          mensaje: `Marcado como 'Cobrado' pero el estado es '${currentExp.estado}'.`,
          tipo: 'Propuesta de Corrección',
          accionSugerida: "Cambiar el estado a uno final como 'COBRADO' o 'Proc Terminados'."
        });
      }
    }
    
    setVerificationResults(issues);
    setIsVerifying(false);
    setIsModalOpen(true);
  };

  const getEstadoColor = (estado: string): ChipColor => {
    switch (estado?.toLowerCase().trim()) {
      case 'cobrado': case 'entregado': return 'success';
      case 'proc terminados': return 'info';
      case 'en gerencia': return 'primary';
      case 'sol cobro a jzdo': return 'secondary';
      case 'pte de clasificar': case 'pendiente': return 'warning';
      case 'convertido': return 'default';
      case 'cancelado': return 'error';
      default: return 'default';
    }
  };
  
  const handleRequestSort = (property: SortableKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" height="80vh"><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const paginatedExpedientes = sortedAndFilteredExpedientes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const headCells: { id: SortableKey; label: string; }[] = [
    { id: 'codigo_actual', label: 'Código Actual' }, { id: 'tipo', label: 'Tipo' }, { id: 'numero', label: 'Número' },
    { id: 'anio', label: 'Año' }, 
    { id: 'cliente_id', label: 'Cliente' },
    { id: 'estado', label: 'Estado' }, { id: 'es_activo', label: 'Es Activo' },
    { id: 'proviene_de', label: 'Proviene de' },
    { id: 'se_ha_convertido_en', label: 'Se ha convertido en' },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
  <Typography variant="h4">Mostrando {sortedAndFilteredExpedientes.length} de {expedientes.length} expedientes</Typography>
  <Box display="flex" gap={1}>
    <Button 
      variant="outlined" 
      startIcon={<Download />}
      onClick={exportToCSV}
      color="secondary"
    >
      Exportar CSV
    </Button>
    <Button variant="outlined" onClick={() => navigate('/')}>
      Volver al Dashboard
    </Button>
  </Box>
</Box>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <Box sx={{ width: { xs: '100%', md: '35%' } }}><TextField fullWidth placeholder="Buscar por código, número, origen o conversión..." onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}/></Box>
          <Box sx={{ width: { xs: '100%', md: '25%' } }}><FormControl fullWidth size="small"><InputLabel>Estado</InputLabel><Select value={selectedEstado} label="Estado" onChange={(e) => setSelectedEstado(e.target.value)}><MenuItem value=""><em>Todos</em></MenuItem>{estadosDisponibles.map(e => (<MenuItem key={e} value={e}>{e}</MenuItem>))}</Select></FormControl></Box>
          {/* Filtro por cliente */}
<Box sx={{ width: { xs: '100%', md: '20%' } }}>
  <FormControl fullWidth size="small">
    <InputLabel>Cliente</InputLabel>
    <Select 
      value={selectedCliente} 
      label="Cliente" 
      onChange={(e) => setSelectedCliente(e.target.value)}
      disabled={loadingClientes}
    >
      <MenuItem value=""><em>Todos</em></MenuItem>
      {clientes.map(cliente => (
        <MenuItem key={cliente.id} value={cliente.id}>
          {cliente.nombre}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
</Box>
          <Box sx={{ width: 'auto' }}><FormControlLabel control={<Checkbox checked={showOnlyActive} onChange={(e) => setShowOnlyActive(e.target.checked)} />} label="Activos"/></Box>
          <Box sx={{ width: 'auto' }}><FormControlLabel control={<Checkbox checked={hidePaid} onChange={(e) => setHidePaid(e.target.checked)} />} label="No cobrados"/></Box>
          <Box sx={{ flexGrow: 1, textAlign: 'right' }}>
            <Button variant="contained" color="secondary" startIcon={isVerifying ? <CircularProgress size={20} color="inherit"/> : <VerifiedUser />} onClick={handleVerifyData} disabled={isVerifying}>
              {isVerifying ? 'Verificando...' : 'Verificar Datos'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (<TableCell key={headCell.id} sortDirection={orderBy === headCell.id ? order : false}><TableSortLabel active={orderBy === headCell.id} direction={orderBy === headCell.id ? order : 'asc'} onClick={() => handleRequestSort(headCell.id)}>{headCell.label}</TableSortLabel></TableCell>))}
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedExpedientes.map((expediente) => {
              const currentExp = expediente as any;
              return (
              <TableRow key={currentExp.id} hover>
                <TableCell>{currentExp.codigo_actual || '-'}</TableCell>
                <TableCell>{currentExp.tipo || '-'}</TableCell>
                <TableCell>{currentExp.numero || '-'}</TableCell>
                <TableCell>{currentExp.anio || '-'}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {getClienteNombre(currentExp.cliente_id)}
                  </Typography>
                </TableCell>
                <TableCell><Chip label={currentExp.estado} color={getEstadoColor(currentExp.estado)} size="small" /></TableCell>
                <TableCell><Chip label={currentExp.es_activo ? 'Activo' : 'Inactivo'} color={currentExp.es_activo ? 'success' : 'error'} size="small" /></TableCell>
                <TableCell>{currentExp.proviene_de || '-'}</TableCell>
                <TableCell>{currentExp.se_ha_convertido_en || '-'}</TableCell>
                <TableCell><Button size="small" startIcon={<Visibility />} onClick={() => navigate(`/expedientes/${currentExp.id}`)}>Ver</Button></TableCell>
              </TableRow>
            )})}
          </TableBody>
        </Table>
        <TablePagination rowsPerPageOptions={[25, 50, 100]} component="div" count={sortedAndFilteredExpedientes.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage="Filas por página:"/>
      </TableContainer>

      {/* --- SOLUCIÓN AQUÍ: Modal rediseñada para mostrar problemas y propuestas --- */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '70%', maxWidth: 900, bgcolor: 'background.paper', border: '2px solid #000', boxShadow: 24, p: 4, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="h2">Resultados de la Verificación</Typography>
            <IconButton onClick={() => setIsModalOpen(false)}><Close /></IconButton>
          </Box>
          <Divider sx={{ my: 2 }}/>
          {verificationResults.length === 0 ? (
            <Box textAlign="center" my={4}>
              <VerifiedUser color="success" sx={{ fontSize: 60 }}/>
              <Typography variant="h5" mt={2}>¡Todo correcto!</Typography>
              <Typography>No se encontraron incongruencias en los datos.</Typography>
            </Box>
          ) : (
            <Box sx={{ overflowY: 'auto' }}>
              <List>
                {verificationResults.map((res, index) => (
                  <ListItem key={index} divider sx={{ alignItems: 'flex-start', py: 2 }}>
                    <ListItemIcon sx={{ mt: 1 }}>
                      {res.tipo === 'Propuesta de Corrección' ? <AutoFixHigh color="primary"/> : <Warning color="error"/>}
                    </ListItemIcon>
                    <ListItemText 
                      primary={<Typography variant="body1"><b>Expediente {res.codigo_actual}:</b> {res.mensaje}</Typography>} 
                      secondary={
                        <Box component="span" sx={{ mt: 1, display: 'block' }}>
                          <Typography variant="body2" component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            {res.accionSugerida ? (
                              <>
                                <Build sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                                <b>Acción sugerida:</b>&nbsp;{res.accionSugerida}
                              </>
                            ) : (
                              <>
                                <Warning sx={{ fontSize: 16, mr: 1, color: 'error.main' }} />
                                <b>{res.tipo}</b>
                              </>
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block', mt: 0.5 }}>
                            ID: {res.expedienteId}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default ExpedientesList;